import {
  ProductPrintPositionAddModel,
  ProductPrintPositionBoxModel,
} from '@/typings';
import { mm2px } from '@/utils';
import {
  ProductVariant as SelectedProductVariant,
  ProductPrintAreaPoint,
  ProductPrintArea,
  PmsColor,
  ProductPrintPosition,
  ProductVariantImage,
  ProductPrintVariant,
  ProductPrintData,
  LaserConfiguration,
  LanyardPrintPositionConfiguration,
} from '@midocean/webapps-shared-domain';
import printConfigService from '@/services/PrintConfigService';
import { useStore as useColorsManagmentStore } from './colorsManagement';
import { defineStore } from 'pinia';
import { useStore as useNotificationStore } from './notification';
import { useStore as useMirrorStore } from './mirror';
import {
  scaleEllipsePrintPosition,
  scalePolygonPrintPosition,
  scaleRectanglePrintPosition,
} from '@/utils/areaHelper';
import {
  EllipseArea,
  PolygonArea,
  PrintArea,
  RectangleArea,
} from '@/web-components/import-wizard-v2/typings/common';
import { Polygon, Vector } from 'collider2d';
import { isConvex } from '@/utils/collisionHelper';
import { UnavailableProductPrintPosition } from '@/web-components/import-wizard-v2/typings/product';
import hexRgb from 'hex-rgb';
import { ProductSnapshot } from '@/typings/historyV2';
import { PanelTargetLine, ProductPrintPanel } from '@/typings/ProductPrintData';
import { BACKGROUND_IMAGE_DPI } from '@/web-components/import-wizard-v2/constants';
import { getTechniqueGroupCode } from '@/utils/validationHelper';
import { PRINTING_GROUPS } from '@/constants/printTechnique';

type EmbroideryConfigurationPayload = {
  textureMix?: number | string;
  textureSpread?: number | string;
  textureThicknes?: number | string;
  textureThickness?: number | string;
  bevel?: number | string;
  bevelAzimuth?: number | string;
  bevelElevation?: number | string;
  bevelContrast?: number | string;
  shadowExtent?: number | string;
  shadowIntensity?: number | string;
};

const parseEmbroideryConfiguration = (
  configString: string
): EmbroideryConfigurationPayload | null => {
  try {
    const parsed = JSON.parse(configString);
    if (parsed && typeof parsed === 'object') {
      return parsed as EmbroideryConfigurationPayload;
    }
    return null;
  } catch {
    return null;
  }
};

export const useStore = defineStore({
  id: 'productV2',

  state: () => {
    return {
      data: {} as ProductPrintData,
      unavailablePrintPositions: [] as UnavailableProductPrintPosition[],
      selectedPrintPosition: {} as ProductPrintPositionBoxModel,
      selectedVariants: [] as SelectedProductVariant[],
      selectedVariant: {} as ProductPrintVariant,
      lastManagedPrintPositionId: '',
      previousManagedPrintPositionId: '',
      editedPositions: [] as string[],
      positionsSetFromProps: false,
      isProductPrintable: true,
      panelPosition: { x: 0, width: 0 } as ProductPrintPanel,
      centerCoordinatesOfPanels: [] as PanelTargetLine[],
      panelsPositions: [] as number[],
      printPositionScaleFactor: 1,
    };
  },

  getters: {
    techniqueSapCodeOnPosition: (state) => {
      return (positionName: string): string => {
        return (
          state.unavailablePrintPositions.find(
            (p) => p.positionName === positionName
          )?.technique.printTechniqueSAPCode || ''
        );
      };
    },

    activePosition: (state) => {
      if (!state.data.id) {
        return {};
      }
      const pp = state.data.productPrintPositions.find((ppp) => {
        return ppp.id === state.selectedPrintPosition.id;
      });

      if (pp === undefined) return {};

      if (state.data.productPrintPositions) {
        const {
          id,
          originalImageWebDpi,
          name,
          printArea,
          originalImageWidthPx,
          originalImageHeightPx,
          originalImageDpi,
          printTechniques,
          categoryConfiguration,
          category,
          maxWidthMm,
          maxHeightMm,
          anchorPoint,
        } = pp;

        let { originalImageUrl } = pp;

        if (state.selectedVariant && state.selectedVariant.images) {
          const imageUrl = state.selectedVariant.images.find((img) => {
            return id === img.printPositionID;
          }) as ProductVariantImage;
          originalImageUrl = imageUrl.imageUrl;
        }

        const limitation = printTechniques.find(
          (x) => x.id == state.selectedPrintPosition.techniqueId
        )?.printTechnique.limitation;

        return {
          id,
          originalImageUrl,
          originalImageDpi,
          originalImageWebDpi,
          name,
          printArea,
          originalImageWidthPx,
          originalImageHeightPx,
          limitation,
          categoryConfiguration,
          category,
          printTechniques,
          anchorPoint,
        };
      }
      return {};
    },
    getDebossingColor: (state) => {
      const variantImage = state.selectedVariant.images?.find(
        (i) => i.printPositionID == state.selectedPrintPosition.id
      );
      if (variantImage && variantImage.mostUsedColor) {
        const debossingColor = hexRgb(variantImage.mostUsedColor);
        return [debossingColor.red, debossingColor.green, debossingColor.blue];
      }
      return [];
    },
    area: (state) => {
      return (positionId: string) => {
        const productPrintPosition = state.data.productPrintPositions.find(
          (p) => p.id === positionId
        );

        if (!productPrintPosition) return [];
        if (!productPrintPosition.printArea.productPrintAreaPoints) return [];

        return [...productPrintPosition.printArea.productPrintAreaPoints]
          .sort((a, b) => (a.sequenceNo > b.sequenceNo ? 1 : -1))
          .map((point: ProductPrintAreaPoint) => {
            return {
              x: mm2px(point['distanceFromLeftEdge'], BACKGROUND_IMAGE_DPI),
              y: mm2px(point['distanceFromTop'], BACKGROUND_IMAGE_DPI),
            };
          });
      };
    },

    isEmbroideryConverter: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );

          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.EMBROIDERY_GROUPS
          );
        } else {
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId =
              state.unavailablePrintPositions.find(
                (pp) => pp.positionId === posId
              )?.technique.id || '';
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.EMBROIDERY_GROUPS
          );
        }
      };
    },

    isLaserConverter: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );

          return getTechniqueGroupCode(technique, PRINTING_GROUPS.LASER_GROUPS);
        } else {
          // if printPositionId is not defined, then we use the one from selectedPrintPosition
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId =
              state.unavailablePrintPositions.find(
                (pp) => pp.positionId === posId
              )?.technique.id || '';
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(technique, PRINTING_GROUPS.LASER_GROUPS);
        }
      };
    },
    isDebossingTechnique: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );

          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.DEBOSSING_GROUPS
          );
        } else {
          // if printPositionId is not defined, then we use the one from selectedPrintPosition
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId = state.unavailablePrintPositions.find(
              (pp) => pp.positionId === posId
            )?.technique.id;
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.DEBOSSING_GROUPS
          );
        }
      };
    },

    isFullColor: (state) => {
      return (printPositionId?: string) => {
        // if printPositionId is not defined, then we use the one from selectedPrintPosition
        let posId = state.selectedPrintPosition.id;
        let techniqueId = state.selectedPrintPosition.techniqueId;

        if (printPositionId !== undefined) {
          posId = printPositionId;
          techniqueId =
            state.unavailablePrintPositions.find(
              (pp) => pp.positionId === posId
            )?.technique.id || '';
        }
        const printPostion = state.data.productPrintPositions?.find(
          (p) => p.id === posId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.id === techniqueId
        );

        return technique?.printTechnique.isFullColor || false;
      };
    },
    isPantoneColorTechnique() {
      return (printPositionId?: string) => {
        // if printPositionId is not defined, then we use the one from selectedPrintPosition
        let posId = this.selectedPrintPosition.id;
        let techniqueId = this.selectedPrintPosition.techniqueId;

        if (printPositionId !== undefined) {
          posId = printPositionId;
          techniqueId =
            this.unavailablePrintPositions.find((pp) => pp.positionId === posId)
              ?.technique.id || '';
        }
        const printPostion = this.data.productPrintPositions?.find(
          (p) => p.id === posId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.id === techniqueId
        );

        if (technique) {
          return (
            !this.isFullColor() &&
            !this.isLaserConverter() &&
            !this.isDebossingTechnique() &&
            !this.isSublimationTechnique
          );
        } else {
          return false;
        }
      };
    },
    isSmallDetailsTechnique: (state) => {
      return (positionId?: string, techCode?: string) => {
        if (!positionId && !techCode) {
          const currentPrintPosition = state.data.productPrintPositions?.find(
            (p) => p.id === state.selectedPrintPosition.id
          );
          const technique = currentPrintPosition?.printTechniques.find(
            (t) => t.id === state.selectedPrintPosition.techniqueId
          );

          return technique?.printTechnique.checkSmallDetails || false;
        }

        const printPostion = state.data.productPrintPositions?.find(
          (p) => p.id === positionId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.printTechnique.code === techCode
        );

        return technique?.printTechnique.limitation !== null || false;
      };
    },
    isSublimationTechnique: (state) => {
      const currentPrintPosition = state.data.productPrintPositions?.find(
        (p) => p.id === state.selectedPrintPosition.id
      );

      const technique = currentPrintPosition?.printTechniques.find(
        (t) => t.id === state.selectedPrintPosition.techniqueId
      );
      return getTechniqueGroupCode(
        technique,
        PRINTING_GROUPS.SUBLIMATION_GROUPS
      );
    },

    isTraditionalTechnique() {
      return (positionId?: string, techCode?: string) => {
        if (!techCode) {
          const currentPrintPosition = this.data.productPrintPositions?.find(
            (p) => p.id === this.selectedPrintPosition.id
          );
          const technique = currentPrintPosition?.printTechniques.find(
            (t) => t.id === this.selectedPrintPosition.techniqueId
          );

          return !technique?.printTechnique.isFullColor || false;
        }

        const printPostion = this.data.productPrintPositions?.find(
          (p) => p.id === positionId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.printTechnique.code === techCode
        );

        return !technique?.printTechnique.isFullColor || false;
      };
    },

    hasAnchorPointOnPrintPosition(state) {
      return (id: string): boolean => {
        const currentPrintPosition = state.data.productPrintPositions?.find(
          (p) => p.id === id
        );
        if (currentPrintPosition && currentPrintPosition.anchorPoint) {
          return true;
        }
        return false;
      };
    },

    rotation(): number {
      const printArea =
        this.activePosition &&
        (this.activePosition.printArea as ProductPrintArea);
      if (printArea) {
        const rotationInDegrees = printArea.rotation as number;
        const rotationInRadians = (rotationInDegrees * Math.PI) / 180;
        return rotationInRadians;
      }
      return 0;
    },

    printPositions: (state): ProductPrintPosition[] => {
      return state.data.productPrintPositions.filter((position) => {
        return state.unavailablePrintPositions.some((p) => {
          return p.positionId === position.id;
        });
      });
    },

    getProductPrintPosition: (state) => {
      return (id: string): ProductPrintPosition | undefined =>
        state.data.productPrintPositions?.find((p) => p.id === id);
    },

    laserVariantColor: (state) => {
      return (
        printPositionId: string,
        techniqueId: string,
        variantSku: string
      ) => {
        const printPosition = state.data.productPrintPositions.find(
          (p) => p.id === printPositionId
        );
        const printTechnique = printPosition?.printTechniques.find(
          (t) => t.id === techniqueId
        );
        const converters = printTechnique?.printTechnique.converters;
        const eskoConverters = printTechnique?.printTechnique.eskoConverters;
        const baseLaser = converters?.find(
          (c) => c.type === 'Laser' && c.variantSku === null
        );
        let laserConfiguration: LaserConfiguration = {} as LaserConfiguration;
        let eskoLaserPmsColor: PmsColor | undefined = undefined;
        const eskoLaserConverter = eskoConverters?.find(
          (c) =>
            c.type === 'Laser' &&
            c.variantSku === variantSku &&
            c.positionName === printPosition?.name
        );

        if (eskoLaserConverter !== undefined) {
          const eskoLaserConfig = JSON.parse(eskoLaserConverter.configuration);
          const colorManagementStore = useColorsManagmentStore();

          eskoLaserPmsColor = colorManagementStore.availablePmsColors.find(
            (x) => x.name === eskoLaserConfig.laserColor
          );

          if (eskoLaserPmsColor) {
            laserConfiguration.laserColor = {
              red: eskoLaserPmsColor.red,
              blue: eskoLaserPmsColor.blue,
              green: eskoLaserPmsColor.green,
            };

            laserConfiguration.laserPercentage =
              eskoLaserConfig.laserPercentage;
          }
        }
        if (eskoLaserPmsColor === undefined) {
          const configString =
            converters?.find(
              (c) => c.type === 'Laser' && c.variantSku === variantSku
            )?.configuration || baseLaser?.configuration;

          laserConfiguration = JSON.parse(configString as string);
        }

        return laserConfiguration;
      };
    },

    getTechniqueMaxColors(state) {
      return (positionId: string, techniqueId: string) => {
        const position = state.data.productPrintPositions.find(
          (x) => x.id === positionId
        );

        const technique = position?.printTechniques.find(
          (x) => x.id === techniqueId
        );

        return technique ? technique.maximumColours : 0;
      };
    },

    isAnyPositionAdded: (state) => {
      return state.unavailablePrintPositions.length > 0;
    },

    getProductName: (state) => {
      return state.data.name;
    },
    getProductSku: (state) => {
      return state.data.sku;
    },
    getPrintPositionScaleFactor: (state) => {
      return state.printPositionScaleFactor;
    },
    getAIGeneratedPhotographyAvailable: (state) => {
      return state.data.productPrintPositions.some(
        (position) => position.aiPreviewPrompt?.length > 0
      );
    },
  },

  actions: {
    isSublimationTechniqueOnPosition(positionId: string): boolean {
      const currentPrintPosition = this.data.productPrintPositions?.find(
        (p) => p.id === positionId
      );
      const technique = currentPrintPosition?.printTechniques.find((t) =>
        t.printTechnique.name.toUpperCase().includes('SUBLIMATION')
      );

      return getTechniqueGroupCode(
        technique,
        PRINTING_GROUPS.SUBLIMATION_GROUPS
      );
    },
    isConverterUsed(positionId?: string, techCode?: string): boolean {
      return (
        this.isLaserConverter(positionId, techCode) ||
        this.isEmbroideryConverter(positionId, techCode) ||
        this.isDebossingTechnique(positionId, techCode) ||
        this.isSublimationTechnique
      );
    },
    isUncoloredConverterUsed(positionId?: string, techCode?: string): boolean {
      return (
        this.isLaserConverter(positionId, techCode) ||
        this.isDebossingTechnique(positionId, techCode) ||
        this.isSublimationTechnique
      );
    },
    async getProduct(productSKU: string) {
      try {
        const { data } = await printConfigService.get(productSKU);
        this.data = data;
        return data;
      } catch (e) {
        console.log(e);
      }
    },

    // drawPoints from old store, you can decide if the area should be scaled or not by providing the isScaled flag
    printPositionArea(
      positionId: string,
      isScaled: boolean
    ): PrintArea | undefined {
      if (this.area(positionId).length === 0) return;

      const productPrintPosition = this.data.productPrintPositions.find(
        (p) => p.id === positionId
      );

      if (productPrintPosition) {
        const { printArea, originalImageWebDpi } = productPrintPosition;

        const rotationInRadians = (printArea.rotation * Math.PI) / 180;

        if (productPrintPosition.printArea.shapeType === 'Rectangle') {
          const originalRectangle = {
            x: this.area(positionId)[0]?.x,
            y: this.area(positionId)[0]?.y,
            width: mm2px(printArea.widthMm, BACKGROUND_IMAGE_DPI),
            height: mm2px(printArea.heightMm, BACKGROUND_IMAGE_DPI),
            rotation: rotationInRadians,
          };

          let distanceOfMirrorPosition = 0;
          let distanceOfMirrorPositionMm = 0;

          if (productPrintPosition?.category == 'Lanyard') {
            const configuration =
              productPrintPosition.categoryConfiguration as LanyardPrintPositionConfiguration;
            distanceOfMirrorPosition = mm2px(
              configuration.distanceOfMirrorPosition,
              BACKGROUND_IMAGE_DPI
            );
            distanceOfMirrorPositionMm = configuration.distanceOfMirrorPosition;
          }

          const scaledArea = scaleRectanglePrintPosition(
            originalRectangle,
            distanceOfMirrorPosition
          );

          this.setPrintPositionScaleFactor(scaledArea.scaleFactor);

          return isScaled
            ? ({
                ...scaledArea,
                kind: 'Rectangle',
                rotation: rotationInRadians,
                isLanyard: productPrintPosition?.category === 'Lanyard',
                distanceOfMirrorPx:
                  distanceOfMirrorPosition * scaledArea.scaleFactor,
                distanceOfMirrorMm: distanceOfMirrorPositionMm,
                scaleFactor: scaledArea.scaleFactor,
              } as RectangleArea)
            : ({
                ...originalRectangle,
                kind: 'Rectangle',
                rotation: rotationInRadians,
                isLanyard: productPrintPosition?.category == 'Lanyard',
                distanceOfMirrorPx: distanceOfMirrorPosition,
                distanceOfMirrorMm: distanceOfMirrorPositionMm,
                scaleFactor: 1,
              } as RectangleArea);
        }

        if (productPrintPosition.printArea.shapeType === 'Ellipse') {
          const originalEllipse = {
            x: (this.area(positionId)[0].x + this.area(positionId)[1].x) / 2,
            y: (this.area(positionId)[0].y + this.area(positionId)[1].y) / 2,
            rx: (this.area(positionId)[1].x - this.area(positionId)[0].x) / 2,
            ry: (this.area(positionId)[1].y - this.area(positionId)[0].y) / 2,
            rotation: rotationInRadians,
          };

          return isScaled
            ? ({
                ...scaleEllipsePrintPosition(originalEllipse),
                kind: 'Ellipse',
                rotation: rotationInRadians,
              } as EllipseArea)
            : ({
                ...originalEllipse,
                kind: 'Ellipse',
                rotation: rotationInRadians,
                scaleFactor: 1,
              } as EllipseArea);
        }

        if (productPrintPosition.printArea.shapeType === 'Polygon') {
          const originalPolygon = this.area(positionId);

          if (!isScaled) {
            return {
              points: [...originalPolygon, originalPolygon[0]],
              kind: 'Polygon',
              rotation: rotationInRadians,
              scaleFactor: 1,
            } as PolygonArea;
          }

          const { points, scaleFactor } = scalePolygonPrintPosition({
            points: originalPolygon,
            rotation: rotationInRadians,
          });

          return {
            points: [...points, points[0]],
            kind: 'Polygon',
            rotation: rotationInRadians,
            scaleFactor,
          } as PolygonArea;
        }
      }

      return;
    },

    setUnavailablePrintPositions(positions: UnavailableProductPrintPosition[]) {
      this.unavailablePrintPositions = [...positions];
    },

    setSelectedVariant(variant: ProductPrintVariant) {
      this.selectedVariant = variant;
    },

    setSelectedVariants(variants: SelectedProductVariant[]) {
      this.selectedVariants = variants;
    },
    setSelectedPrintPosition(positionId: string) {
      const productPrintPosition = this.getProductPrintPosition(positionId);

      const positionBoxModel = this.unavailablePrintPositions.find(
        (p) => p.positionId === positionId
      );

      if (!productPrintPosition || !positionBoxModel) {
        this.selectedPrintPosition = {} as ProductPrintPositionBoxModel;
      } else {
        this.selectedPrintPosition = {
          id: productPrintPosition.id,
          name: productPrintPosition.name,
          originalImageUrl: productPrintPosition.originalImageUrl,
          originalImageDpi: productPrintPosition.originalImageDpi,
          originalImageWebUrl: productPrintPosition.originalImageWebUrl,
          originalImageWebDpi: productPrintPosition.originalImageWebDpi,
          techniqueName: positionBoxModel.technique.techniqueName,
          techniqueId: positionBoxModel.technique.id,
          maximumColours: productPrintPosition.printTechniques.find(
            (technique) => technique.id === positionBoxModel.technique.id
          )?.maximumColours,
          maxWidthMm: productPrintPosition.maxWidthMm,
          maxHeightMm: productPrintPosition.maxHeightMm,
        };
        this.lastManagedPrintPositionId = productPrintPosition.id;
      }
    },
    setPreviousManagedPrintPositionId(id: string) {
      this.previousManagedPrintPositionId = id;
    },

    setActivePrintPosition(position: ProductPrintPositionAddModel) {
      const activePosition = this.printPositions?.find((p) => {
        return p.id === position.positionId;
      });

      if (!activePosition) {
        this.selectedPrintPosition = {} as ProductPrintPositionBoxModel;
      } else {
        this.selectedPrintPosition = {
          id: activePosition.id,
          name: activePosition.name,
          originalImageUrl: activePosition.originalImageUrl,
          originalImageDpi: activePosition.originalImageDpi,
          originalImageWebUrl: activePosition.originalImageWebUrl,
          originalImageWebDpi: activePosition.originalImageWebDpi,
          techniqueName: position.technique.techniqueName,
          techniqueId: position.technique.id,
          maximumColours: activePosition.printTechniques.find((technique) => {
            return technique.id === position?.technique.id;
          })?.maximumColours,
          maxWidthMm: activePosition.maxWidthMm,
          maxHeightMm: activePosition.maxHeightMm,
        };
        this.lastManagedPrintPositionId = activePosition.id;
      }
    },

    async deletePrintPosition(id: string) {
      console.log('ID', id, this.unavailablePrintPositions);

      if (!this.unavailablePrintPositions.some((p) => p.positionId === id)) {
        console.warn('Invalid id provided. Cannot delete entity.');
        return;
      }
      /**
       * If we just deleted the last managed remove the reference as well
       */
      if (this.lastManagedPrintPositionId === id) {
        this.lastManagedPrintPositionId = '';
      }
      const removeIndex = this.unavailablePrintPositions.findIndex(
        (p) => p.positionId === id
      );

      /**
       * Remove item from list of unavailable print positions,
       * thus making it available again
       */
      this.unavailablePrintPositions.splice(removeIndex, 1);

      /**
       * If we still have some in list show/select the first one
       */
      if (this.unavailablePrintPositions.length > 0) {
        this.setSelectedPrintPosition(
          this.unavailablePrintPositions[0].positionId
        );
      } else {
        /**
         * Other wise just reset the dropdown box
         */
        this.setSelectedPrintPosition('');
      }
      const exists = this.editedPositions.some((p) => p === id);
      if (!exists) {
        this.editedPositions.push(id);
      }

      const notificationStore = useNotificationStore();
      // notificationStore.resetNotificationsForBell(id);

      const mirrorStore = useMirrorStore();
      mirrorStore.removeMirrorShapes(id);
    },
    getVariantLaserColor(
      printPositionId: string,
      techniqueId: string,
      variantSku: string
    ) {
      const printPosition = this.data.productPrintPositions.find(
        (p) => p.id === printPositionId
      );

      const printTechnique = printPosition?.printTechniques.find(
        (t) => t.id === techniqueId
      );

      const converters = printTechnique?.printTechnique.converters;

      const eskoConverters = printTechnique?.printTechnique.eskoConverters;

      const baseLaser = converters?.find(
        (c) => c.type === 'Laser' && c.variantSku === null
      );

      let laserConfiguration: LaserConfiguration = {} as LaserConfiguration;

      let eskoLaserPmsColor: PmsColor | undefined = undefined;

      const eskoLaserConverter = eskoConverters?.find(
        (c) =>
          c.type === 'Laser' &&
          c.variantSku === variantSku &&
          c.positionName === printPosition?.name
      );

      if (eskoLaserConverter !== undefined) {
        const eskoLaserConfig = JSON.parse(eskoLaserConverter.configuration);
        const colorManagementStore = useColorsManagmentStore();

        eskoLaserPmsColor = colorManagementStore.availablePmsColors.find(
          (x) => x.name === eskoLaserConfig.laserColor
        );

        if (eskoLaserPmsColor) {
          laserConfiguration.laserColor = {
            red: eskoLaserPmsColor.red,
            blue: eskoLaserPmsColor.blue,
            green: eskoLaserPmsColor.green,
          };

          laserConfiguration.laserPercentage = eskoLaserConfig.laserPercentage;
        }
      }
      if (eskoLaserPmsColor === undefined) {
        const configString =
          converters?.find(
            (c) => c.type === 'Laser' && c.variantSku === variantSku
          )?.configuration || baseLaser?.configuration;

        laserConfiguration = JSON.parse(configString as string);
      }

      return laserConfiguration;
    },

    getVariantEmbroideryConfiguration(
      printPositionId: string,
      techniqueId: string,
      variantSku: string
    ) {
      const defaultEmbroideryConfiguration = {
        textureMix: 0,
        textureSpread: 0,
        textureThicknes: 0,
        bevel: 0,
        bevelAzimuth: 0,
        bevelElevation: 0,
        bevelContrast: 0,
        shadowExtent: 0,
        shadowIntensity: 0,
      };

      const printPosition = this.data.productPrintPositions.find(
        (p) => p.id === printPositionId
      );
      const printTechnique = printPosition?.printTechniques.find(
        (t) => t.id === techniqueId
      );

      const converters = printTechnique?.printTechnique.converters;
      const baseEmbroidery = converters?.find(
        (c) => c.type === 'Embroidery' && c.variantSku === null
      );

      const configString =
        converters?.find(
          (c) => c.type === 'Embroidery' && c.variantSku === variantSku
        )?.configuration || baseEmbroidery?.configuration;

      if (!configString) {
        return defaultEmbroideryConfiguration;
      }
      const parsedConfiguration = parseEmbroideryConfiguration(configString);
      if (!parsedConfiguration) {
        return defaultEmbroideryConfiguration;
      }

      return {
        textureMix: Number(parsedConfiguration.textureMix) || 0,
        textureSpread: Number(parsedConfiguration.textureSpread) || 0,
        textureThicknes:
          Number(
            parsedConfiguration.textureThicknes ??
              parsedConfiguration.textureThickness
          ) || 0,
        bevel: Number(parsedConfiguration.bevel) || 0,
        bevelAzimuth: Number(parsedConfiguration.bevelAzimuth) || 0,
        bevelElevation: Number(parsedConfiguration.bevelElevation) || 0,
        bevelContrast: Number(parsedConfiguration.bevelContrast) || 0,
        shadowExtent: Number(parsedConfiguration.shadowExtent) || 0,
        shadowIntensity: Number(parsedConfiguration.shadowIntensity) || 0,
      };
    },

    polygonDetails(positionId: string) {
      const vectors = [];

      const polygonArea = this.printPositionArea(
        positionId,
        true
      ) as PolygonArea;

      if (polygonArea?.points) {
        for (let i = 0; i < polygonArea.points.length; i++) {
          vectors.push(
            new Vector(polygonArea.points[i].x, polygonArea.points[i].y)
          );
        }
      }
      const polygon = new Polygon(new Vector(0, 0), vectors);
      const centroid = polygon.getCentroid();

      let min = Number.MAX_VALUE;

      if (!isConvex(polygon)) {
        const points = [...polygon.points];
        for (let i = 0; i < points.length; i++) {
          const point = points[i].clone();
          const distanceToPoint = point.sub(centroid).len();
          if (distanceToPoint < min) {
            min = distanceToPoint;
          }
        }
      } else {
        const points = [...polygon.points];
        for (let i = 0; i < points.length; i++) {
          let next = i + 1;
          if (next >= points.length) {
            next = 0;
          }

          // calculate slope ---> (y2-y1)/(x2-x1)
          const k =
            (points[next].y - points[i].y) / (points[next].x - points[i].x);

          // calcualte n --->  y = kx + n; n = y - kx;
          const n = points[i].y - k * points[i].x;

          // transform to Ax + By + C = 0 --> - kx + y - n = 0;
          const a = -k;
          const b = 1;
          const c = -n;

          // calculate distance ---> d = abs(a*x0 + b*y0 + c) / sqrt(a*a + b*b)
          const distanceToSide = Math.abs(
            (a * centroid.x + b * centroid.y + c) / Math.sqrt(a * a + b * b)
          );
          if (distanceToSide < min) {
            min = distanceToSide;
          }
        }
      }
      return {
        x: centroid.x,
        y: centroid.y,
        minDistance: min * 1.5,
      };
    },

    updateProductStateFromSnapshot(snapshot: ProductSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.unavailablePrintPositions = snapshot.unavailablePrintPositions;
          state.selectedPrintPosition = snapshot.selectedPrintPosition;
          state.lastManagedPrintPositionId =
            snapshot.lastManagedPrintPositionId;
          state.previousManagedPrintPositionId =
            snapshot.previousManagedPrintPositionId;
        }
      });
    },
    setPanelPosition(position: ProductPrintPanel) {
      this.panelPosition = position;
    },
    setCenterCoordinatesOfPanels(coordinates: PanelTargetLine[]) {
      this.centerCoordinatesOfPanels = coordinates;
    },
    setPanelsPositions(panelsPositions: number[]) {
      this.panelsPositions = panelsPositions;
    },
    setPrintPositionScaleFactor(scaleFactor: number) {
      this.printPositionScaleFactor = scaleFactor;
    },
    resetProductStateToDefault() {
      this.$patch((state) => {
        state.data = {} as ProductPrintData;
        state.unavailablePrintPositions = [] as ProductPrintPositionAddModel[];
        state.selectedPrintPosition = {} as ProductPrintPositionBoxModel;
        state.lastManagedPrintPositionId = '';
        state.previousManagedPrintPositionId = '';
        state.positionsSetFromProps = false;
        state.selectedVariants = [] as SelectedProductVariant[];
        state.selectedVariant = {} as ProductPrintVariant;
        state.isProductPrintable = true;
        state.editedPositions = [] as string[];
        state.panelPosition = { x: 0, width: 0 } as ProductPrintPanel;
        state.centerCoordinatesOfPanels = [] as PanelTargetLine[];
        state.panelsPositions = [] as number[];
        state.printPositionScaleFactor = 1;
      });
    },
  },
});
